import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Gestion du scroll au changement de route React Router v6.
 *
 * Deux comportements en fonction de l'URL :
 *
 * 1. PAS de hash → reset au top (window.scrollTo 0, 0). React Router v6 ne le fait
 *    pas par défaut, ce qui cause le bug "j'atterris en bas du formulaire register
 *    après un clic CTA depuis le pricing".
 *
 * 2. AVEC hash (#how, #pricing, #faq…) → scrollIntoView sur l'élément ciblé. React
 *    Router v6 n'honore pas non plus les ancres natives, d'où le bug : /#pricing
 *    chargeait en haut au lieu de scroller à la grille tarifs.
 *
 * Race conditions :
 * - Au mount initial (deep-link `/?#pricing` collé dans nouvel onglet), l'élément
 *   peut ne pas encore être monté quand le useEffect tourne. On attend 2 frames
 *   (requestAnimationFrame × 2) pour laisser React peindre, puis on tente.
 * - Si l'élément n'est toujours pas là (rendu conditionnel, lazy load, etc.) on
 *   retry une fois après 200ms. Au-delà : on abandonne sans bruit, plutôt que de
 *   spammer le DOM.
 *
 * Behavior 'instant' : pas d'animation, navigation transparente.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return;
    }

    const id = hash.slice(1);
    if (!id) return;

    let canceled = false;
    let retryTimer = null;

    const tryScroll = () => {
      if (canceled) return false;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'instant', block: 'start' });
        return true;
      }
      return false;
    };

    // 2 rAF pour laisser React peindre, puis retry à 200ms si élément pas encore là
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!tryScroll()) {
          retryTimer = setTimeout(tryScroll, 200);
        }
      });
    });

    return () => {
      canceled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [pathname, hash]);

  return null;
}
